import type { Decimal } from './types';

/**
 * Display helpers.
 *
 * Everything here takes the API's decimal strings and produces text. Nothing here does
 * arithmetic: a NUMERIC(38,18) does not survive a round trip through a JavaScript number, and
 * the whole point of D-07 is that the figures agree with a broker statement to the cent. If a
 * derived number is needed, the server computes it.
 */

/** Parses only for formatting. Never store or send the result back. */
function toNumber(value: Decimal | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatMoney(
  value: Decimal | null | undefined,
  currency: string,
  locale = 'en-GB',
): string | null {
  const parsed = toNumber(value);
  if (parsed === null) {
    return null;
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(parsed);
}

export function formatPercent(
  value: Decimal | null | undefined,
  locale = 'en-GB',
  digits = 2,
): string | null {
  const parsed = toNumber(value);
  if (parsed === null) {
    return null;
  }
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(parsed)}%`;
}

/**
 * Quantities keep their significant decimals. A crypto balance is not two decimal places, and
 * rounding 0.00000001 BTC to 0.00 on screen would contradict FR-C3 in the one place the user
 * actually looks.
 */
export function formatQuantity(value: Decimal | null | undefined, locale = 'en-GB'): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;
  const parsed = toNumber(trimmed);
  if (parsed === null) {
    return trimmed;
  }
  const decimals = trimmed.includes('.') ? (trimmed.split('.')[1]?.length ?? 0) : 0;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(decimals, 8),
  }).format(parsed);
}

export function formatDate(iso: string | null | undefined, locale = 'en-GB'): string | null {
  if (!iso) {
    return null;
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
}

/** Positive, negative or neither - for colouring a figure without re-parsing it everywhere. */
export function signOf(value: Decimal | null | undefined): 'up' | 'down' | 'flat' | null {
  const parsed = toNumber(value);
  if (parsed === null) {
    return null;
  }
  return parsed > 0 ? 'up' : parsed < 0 ? 'down' : 'flat';
}

export function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

export function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The difference between two base-currency totals, as a decimal string, for display.
 *
 * This is the one place the client subtracts money, and it is deliberately narrow. It is
 * safe here because both operands are totals in one currency at cent scale, where a
 * float64 carries about fifteen significant digits - far more than any portfolio total
 * needs - and the result is immediately rounded to two decimals for a label.
 *
 * It is NOT safe on quantities: a crypto balance carries eighteen decimals and would lose
 * precision here, which is the whole reason FR-C3 exists. Do not widen this function.
 *
 * The right home for a day-over-day delta is the server, beside the snapshots it compares.
 * Until that endpoint exists this computes it for one tile.
 */
export function differenceForDisplay(
  current: Decimal | null | undefined,
  previous: Decimal | null | undefined,
): Decimal | null {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }
  const a = Number(current);
  const b = Number(previous);
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return null;
  }
  return (a - b).toFixed(2);
}

/** The same difference as a percentage of the earlier figure. Null when it was zero. */
export function percentChangeForDisplay(
  current: Decimal | null | undefined,
  previous: Decimal | null | undefined,
): Decimal | null {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }
  const a = Number(current);
  const b = Number(previous);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) {
    return null;
  }
  return (((a - b) / Math.abs(b)) * 100).toFixed(2);
}
